import React, { useContext, useMemo, useState } from 'react';
import CurrentUserContext from '../../../../../contexts/CurrentUserContext';
import getIdentifier from '../../../../../getIdentifier';
import getTimeStamp from '../../../../../getTimeStamp';
import useSaveWidget from '../../../hooks/useSaveWidget';
import DatasetDefinitionsContext from '../../../../../contexts/DatasetDefinitionsContext';
import WidgetFooter from '../../WidgetFooter';
import FormInput from '../../../../../kingpin/forms/FormInput';
import { WidgetWrapper } from '../MetricSlideoutWizard/styles';
import Inputs from 'components/Inputs';
import ShadowedList from '../../../../ShadowedList';
import FactTableFieldsInput from '../FactTableFieldsInput';
import HeaderTotalFieldInput from '../HeaderTotalFieldInput';
import RenderModeInput from '../RenderModeInput';

export const useBonusConfigOptions = ({
  editingSection,
  lockedBonusConfigId,
  isProgramOffered,
}: {
  editingSection?:
    | PerformanceBoardTypes.SlideOut.BonusEligibilitySection
    | PerformanceBoardTypes.SlideOut.BonusTotalRewardSection
    | PerformanceBoardTypes.SlideOut.BonusRewardSection
    | PerformanceBoardTypes.SlideOut.RecognitionPaymentsSection;
  lockedBonusConfigId?: string;
  // Narrows the programs offered, e.g. to those enabling a feature.
  isProgramOffered?: (program: BonusTypes.BonusProgram) => boolean;
}) => {
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const [bonusConfigId, setBonusConfigID] = useState<string | undefined>(
    editingSection ? editingSection.bonusConfigId : lockedBonusConfigId,
  );

  const bonusConfigOptions = useMemo((): DropdownOption[] => {
    return bonusPrograms
      .filter(
        (program) =>
          program.name !== undefined &&
          (lockedBonusConfigId === undefined ||
            program.id === lockedBonusConfigId) &&
          (isProgramOffered === undefined || isProgramOffered(program)),
      )
      .map((program) => ({
        label: program.name,
        key: program.id,
        isSelected: program.id === bonusConfigId,
        onSelected: () => {
          setBonusConfigID(program.id);
        },
      }));
  }, [bonusConfigId, bonusPrograms, isProgramOffered, lockedBonusConfigId]);

  return {
    bonusConfigId,
    bonusConfigOptions,
  };
};

const BonusTotalRewardWizard = ({
  close,
  editingSection,
  lockedBonusConfigId,
}: {
  close: () => void;
  editingSection?: PerformanceBoardTypes.SlideOut.BonusTotalRewardSection;
  lockedBonusConfigId?: string;
}) => {
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { bonusConfigId, bonusConfigOptions } = useBonusConfigOptions({
    editingSection,
    lockedBonusConfigId,
  });

  const [factTableFields, setFactTableFields] = useState<string[]>(
    editingSection?.factTableFields ?? [],
  );
  const [totalValueField, setTotalValueField] = useState<string | undefined>(
    editingSection?.totalValueField,
  );
  const [renderMode, setRenderMode] = useState<
    'auto' | 'table' | 'cards' | undefined
  >(editingSection?.renderMode);

  const title = useMemo(() => {
    const selectedConfig = bonusConfigOptions.find((o) => o.isSelected);
    if (selectedConfig) {
      return selectedConfig.label;
    }

    return '';
  }, [bonusConfigOptions]);

  const draftSection = useMemo(():
    | PerformanceBoardTypes.SlideOut.BonusTotalRewardSection
    | undefined => {
    if (!bonusConfigId) {
      return undefined;
    }
    return {
      id: editingSection ? editingSection.id : getIdentifier(),
      sectionType: 'Bonus Total Reward',
      title,
      bonusConfigId,
      factTableFields,
      totalValueField,
      renderMode,
      createdOn: editingSection ? editingSection.createdOn : getTimeStamp(),
      createdBy: editingSection ? editingSection.createdBy : currentUserId,
      updatedBy: currentUserId,
      updatedOn: getTimeStamp(),
    };
  }, [
    bonusConfigId,
    currentUserId,
    editingSection,
    title,
    factTableFields,
    totalValueField,
    renderMode,
  ]);

  const saveWidget = useSaveWidget({
    section: draftSection,
    editingSection,
  });

  return (
    <>
      <ShadowedList>
        <WidgetWrapper>
          <FormInput label={'Bonus Program'}>
            <Inputs.Dropdown options={bonusConfigOptions} />
          </FormInput>
          <FactTableFieldsInput
            bonusConfigId={bonusConfigId}
            fields={factTableFields}
            setFields={setFactTableFields}
          />
          <HeaderTotalFieldInput
            bonusConfigId={bonusConfigId}
            value={totalValueField}
            setValue={setTotalValueField}
          />
          <RenderModeInput value={renderMode} setValue={setRenderMode} />
        </WidgetWrapper>
      </ShadowedList>

      <WidgetFooter
        saveWidget={saveWidget}
        sectionType="Bonus Total Reward"
        isDisabled={draftSection === undefined}
        close={close}
      />
    </>
  );
};

export default BonusTotalRewardWizard;
